import {
  buildParqYesAnswersFromAnamnesis,
  getParqAnswerLabel,
} from '../constants/parqQuestions';
import type { ParqYesAnswer, ParqYesAnswers } from '../types/lead';

function normalizeDetail(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeAnswer(value: unknown): ParqYesAnswer | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const field = typeof record.field === 'string' ? record.field.trim() : '';
  const label = typeof record.label === 'string' ? record.label.trim() : '';

  if (!field) {
    return null;
  }

  const normalized: ParqYesAnswer = {
    field,
    label: label || getParqAnswerLabel({ field, label: '' }),
    detail: normalizeDetail(record.detail),
  };

  return normalized;
}

function normalizeAnswersArray(value: unknown): ParqYesAnswer[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(normalizeAnswer)
    .filter((answer): answer is ParqYesAnswer => answer !== null);
}

export function mapParqYesAnswersFromJson(value: unknown): ParqYesAnswers {
  if (typeof value === 'string') {
    try {
      return mapParqYesAnswersFromJson(JSON.parse(value));
    } catch {
      return { has_yes_answers: false, answers: [] };
    }
  }

  if (Array.isArray(value)) {
    const answers = normalizeAnswersArray(value);
    return {
      has_yes_answers: answers.length > 0,
      answers,
    };
  }

  if (typeof value !== 'object' || value === null) {
    return { has_yes_answers: false, answers: [] };
  }

  const record = value as Record<string, unknown>;
  const answers = normalizeAnswersArray(record.answers);

  return {
    has_yes_answers: record.has_yes_answers === true || answers.length > 0,
    answers,
  };
}

export { buildParqYesAnswersFromAnamnesis, getParqAnswerLabel };
